import {
  enumCurrencyCode,
  enumLoadStatus,
  enumUnit,
  LoadStatus,
  MoneyInput,
  Unit,
  Weight,
} from '@rematter/frontend-lib-api-client';
import {
  FooterItem,
  humanReadableNumber,
  MeasuredValue,
  Money,
  Row,
} from '@rematter/ui-web';

import { LoadStatusTransition } from '~/modules/trade/_components/loads/LoadStatusTransition';
import { useLoadLineItemsTotals } from '~/modules/trade/hooks/useLoadLineItemsTotals';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadFormLineItem, LoadRecord } from '~/modules/trade/types/load';
import { getLocale } from '~/shared/utils/locale';

const isPreShipment = (status: LoadStatus): boolean =>
  status === enumLoadStatus.Unassigned || status === enumLoadStatus.Pending;

const isPostShipment = (status: LoadStatus): boolean =>
  status === enumLoadStatus.Shipped ||
  status === enumLoadStatus.Reconciled ||
  status === enumLoadStatus.Voided;

type LoadDetailWeightCellProps = {
  label: string;
  weight: Weight | null;
  eachCount?: number;
  pricingUnit: Unit;
};

// ⚛️ ⚛️ ⚛️ LOAD DETAIL WEIGHT CELL ⚛️ ⚛️ ⚛️
const LoadDetailWeightCell: React.FC<LoadDetailWeightCellProps> = ({
  label,
  weight,
  eachCount = 0,
  pricingUnit,
}) => {
  const hasWeight = weight !== null && weight.value > 0;
  const hasEach = eachCount > 0;

  return (
    <FooterItem label={label} className="@LoadDetailWeightCell">
      <Row gap2 xCenter>
        {hasWeight && (
          <div className="text-lg font-bold">
            <MeasuredValue value={weight.value} unit={pricingUnit} />
          </div>
        )}
        {hasEach && (
          <div className="text-lg font-bold">
            <MeasuredValue value={eachCount} unit={enumUnit.Each} />
          </div>
        )}
        {!hasWeight && !hasEach && (
          <div className="text-lg font-bold">
            <MeasuredValue value={0} unit={pricingUnit} />
          </div>
        )}
      </Row>
    </FooterItem>
  );
};

type Props = {
  load: LoadRecord;
  numberOfMaterials: number;
  numberOfTags: number;
  requestedLineItems: LoadFormLineItem[];
  shippedLineItems: LoadFormLineItem[];
  salesOrderLineItems: LoadFormLineItem[] | undefined;
  facilityDefaultUnit: Unit;
  onRefetch?: () => void;
};

// ⚛️ ⚛️ ⚛️ LOAD DETAIL FOOTER ⚛️ ⚛️ ⚛️
export const LoadDetailFooter: React.FC<Props> = ({
  load,
  numberOfMaterials,
  numberOfTags,
  requestedLineItems,
  shippedLineItems,
  salesOrderLineItems,
  facilityDefaultUnit,
  onRefetch,
}) => {
  const t = useTranslations();
  const preShipment = isPreShipment(load.status);
  const postShipment = isPostShipment(load.status);

  const requestedTotals = useLoadLineItemsTotals({
    lineItems: requestedLineItems,
    facilityDefaultUnit,
  });

  const shippedTotals = useLoadLineItemsTotals({
    lineItems: shippedLineItems,
    facilityDefaultUnit,
  });

  const soTotals = useLoadLineItemsTotals({
    lineItems: salesOrderLineItems ?? [],
    facilityDefaultUnit,
  });

  const shippedTotalMoney: MoneyInput = {
    amount: shippedTotals.totalEstimatedPrice,
    currencyCode: load.currencyCode ?? enumCurrencyCode.USD,
  };

  return (
    <Row
      xEnd
      yCenter
      gap5
      className="@LoadDetailFooter px-5 py-2 [&>*]:min-h-10"
    >
      <FooterItem label={t.loadDetail.footer.numberOfMaterials}>
        <p className="text-lg font-bold uppercase">
          {humanReadableNumber(numberOfMaterials, {
            decimalPlaces: 0,
            locale: getLocale(),
          })}
        </p>
      </FooterItem>
      {!postShipment && (
        <FooterItem label={t.loadDetail.footer.numberOfTags}>
          <p className="text-lg font-bold uppercase">
            {humanReadableNumber(numberOfTags, {
              decimalPlaces: 0,
              locale: getLocale(),
            })}
          </p>
        </FooterItem>
      )}
      {!postShipment && salesOrderLineItems && (
        <LoadDetailWeightCell
          label={t.loadDetail.footer.salesOrderNetWeight}
          weight={soTotals.netWeight}
          eachCount={soTotals.totalEachCount}
          pricingUnit={soTotals.pricingUnit}
        />
      )}
      {!postShipment && salesOrderLineItems && (
        <LoadDetailWeightCell
          label={t.loadDetail.footer.salesOrderWeightOwed}
          weight={soTotals.netWeightOwed}
          eachCount={soTotals.netEachOwed}
          pricingUnit={soTotals.pricingUnit}
        />
      )}
      <LoadDetailWeightCell
        label={t.loadDetail.footer.requestedWeight}
        weight={requestedTotals.requestedWeight}
        eachCount={requestedTotals.requestedEachCount}
        pricingUnit={requestedTotals.pricingUnit}
      />
      {!preShipment && (
        <LoadDetailWeightCell
          label={t.loadDetail.footer.netShippedWeight}
          weight={shippedTotals.netWeight}
          eachCount={shippedTotals.totalEachCount}
          pricingUnit={shippedTotals.pricingUnit}
        />
      )}
      {!preShipment && (
        <FooterItem label={t.loadDetail.footer.shippedTotal}>
          <Money {...shippedTotalMoney} className="text-lg font-bold" />
        </FooterItem>
      )}

      <LoadStatusTransition
        load={load}
        displayOnlyMarkAsButton
        popOverDirection="top"
        revertButtonVisibility="hidden"
        onRefetch={onRefetch}
      />
    </Row>
  );
};
